import { useTranslation } from 'react-i18next';

import { createFileRoute } from '@tanstack/react-router';

import { SimpleGrid, TabsPageLayoutView } from '@optiro/ui';

import CategoryManager from '@/components/CategoryManager';
import CompanyAccountCategoriesList from '@/components/CompanyAccountCategoriesList';
import { CompanyLogoSettingsCard } from '@/components/CompanyLogoSettingsCard/CompanyLogoSettingsCard';
import { useUserData } from '@/hooks/useUserData/useUserData';
import { Billing } from '@/modules/Billing/Billing';
import { ActivityCategories } from '@/modules/Settings/ActivityCategories/ActivityCategories';
import { AiUsageOrchestrator } from '@/modules/Settings/AiUsage/AiUsage.orchestrator';
import Teams from '@/modules/Settings/Teams/Teams';

export const Route = createFileRoute('/app/settings/')({
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('settings');
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();
  const currentTab = tab || 'general';
  const { data } = useUserData();
  const isAdministrator = data?.role === 'administrator';

  return (
    <TabsPageLayoutView
      currentTab={currentTab}
      title={t('title')}
      subtitle={data?.company?.name || ''}
      onTabChange={(value) => navigate({ search: { tab: value || undefined } })}
      tabs={[
        { label: t('tabs.general'), value: 'general' },
        { label: t('tabs.categories'), value: 'categories' },
        ...(isAdministrator ? [{ label: t('tabs.teams'), value: 'teams' }] : []),
        ...(isAdministrator ? [{ label: t('tabs.aiUsage'), value: 'ai-usage' }] : []),
        { label: t('tabs.billing'), value: 'billing' },
      ]}
      views={[
        {
          value: 'general',
          content: (
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
              <CompanyLogoSettingsCard />
            </SimpleGrid>
          ),
        },
        {
          value: 'categories',
          content: (
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
              <CompanyAccountCategoriesList />
              <CategoryManager />
              <ActivityCategories />
            </SimpleGrid>
          ),
        },
        ...(isAdministrator ? [{ value: 'teams', content: <Teams /> }] : []),
        ...(isAdministrator ? [{ value: 'ai-usage', content: <AiUsageOrchestrator /> }] : []),
        { value: 'billing', content: <Billing /> },
      ]}
    />
  );
}
